// src/lib/richText/styles.ts
//
// How a rich text block looks, in one place. The hosted page and the builder
// preview run under Tailwind's reset, which strips heading sizes, list markers
// and paragraph gaps; a customer's own site has its own CSS, so the embed
// cannot rely on either. Inline styles survive both, and one definition keeps
// the React renderer (RichText.tsx), the embed script (kiroku-embed.ts) and
// the HTML-mode builder (formEmbedUtils.ts) from drifting apart.
//
// Found 2026-10-17 in the task 2.4 screenshot of the hosted page: a heading
// looked like body text and a numbered list had no numbers.
// tests/guard/rich-text-blocks-keep-their-look.test.ts guards all three.

export type RichTextStyleKey =
  | "paragraph"
  | "heading"
  | "subheading"
  | "bulletList"
  | "numberedList";

export const RICH_TEXT_STYLES: Record<
  RichTextStyleKey,
  Record<string, string | number>
> = {
  paragraph: { margin: "0 0 0.5em" },
  heading: {
    margin: "1em 0 0.25em",
    fontSize: "1.125em",
    fontWeight: 600,
    lineHeight: 1.3,
  },
  subheading: {
    margin: "0.75em 0 0.25em",
    fontSize: "1em",
    fontWeight: 600,
    lineHeight: 1.3,
  },
  bulletList: {
    margin: "0.5em 0",
    paddingLeft: "1.25em",
    listStyleType: "disc",
  },
  numberedList: {
    margin: "0.5em 0",
    paddingLeft: "1.25em",
    listStyleType: "decimal",
  },
};

function kebab(name: string): string {
  return name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

/** The same style as a CSS declaration string, for HTML-mode output. */
export function richTextStyleAttr(key: RichTextStyleKey): string {
  return Object.entries(RICH_TEXT_STYLES[key])
    .map(([name, value]) => `${kebab(name)}:${value}`)
    .join(";");
}
